/**
 * The Legal Entities route table (FSD 1.2.3).
 *
 * `web/src/router/index.ts` and `web/src/nav/parts/p01-enterprise.ts` are
 * shared files eight sibling C14 packets also touch and this packet does NOT
 * own — the orchestrator spreads `LEGAL_ENTITY_ROUTES` into the router and
 * adds the nav entry once every packet in this batch has landed.
 *
 * Every route is gated on the **read** permission: creating, editing, mapping
 * a ledger, assigning a balancing segment, designating and dissolving are all
 * checked on the action itself (1.2.3.11: "no single role can complete an
 * entity end to end"), so an Auditor holding only `org.legal_entity.read` can
 * open the register and every entity and change nothing.
 */

import type { RouteRecordRaw } from 'vue-router'

import { LEGAL_ENTITY_PERMISSIONS } from '@/modules/enterprise/legal-entities/permissions'

export const LEGAL_ENTITIES_BASE = '/enterprise/legal-entities'

export function legalEntitiesRoute(): string {
  return LEGAL_ENTITIES_BASE
}

export function legalEntityRoute(id: number): string {
  return `${LEGAL_ENTITIES_BASE}/${id}`
}

export function ownershipRoute(): string {
  return `${LEGAL_ENTITIES_BASE}/ownership`
}

export function intercompanyRoute(): string {
  return `${LEGAL_ENTITIES_BASE}/intercompany`
}

export function eliminationRulesRoute(): string {
  return `${LEGAL_ENTITIES_BASE}/elimination-rules`
}

/*
 * The three 1.2.3 sub-screens are static paths declared BEFORE the `:id`
 * detail route. The detail route's `\d+` constraint already keeps them apart,
 * but order is the belt to that brace.
 *
 * Ownership is gated on 1.2.3.11's narrower `ownership_read` (the graph is
 * "excluded from general reporting scopes"). Intercompany and elimination
 * rules are gated on `intercompany_read` (the server's list gate); create and
 * simulate need `intercompany_manage` and are checked on the action itself.
 */
export const LEGAL_ENTITY_ROUTES: RouteRecordRaw[] = [
  {
    path: LEGAL_ENTITIES_BASE,
    name: 'enterprise-legal-entities',
    component: () => import('@/modules/enterprise/legal-entities/LegalEntitiesListView.vue'),
    meta: {
      titleKey: 'enterprise.legalEntities.routeTitles.list',
      permission: LEGAL_ENTITY_PERMISSIONS.read,
    },
  },
  {
    path: `${LEGAL_ENTITIES_BASE}/ownership`,
    name: 'enterprise-ownership',
    component: () => import('@/modules/enterprise/legal-entities/OwnershipView.vue'),
    meta: {
      titleKey: 'enterprise.legalEntities.routeTitles.ownership',
      permission: LEGAL_ENTITY_PERMISSIONS.ownershipRead,
    },
  },
  {
    path: `${LEGAL_ENTITIES_BASE}/intercompany`,
    name: 'enterprise-intercompany',
    component: () => import('@/modules/enterprise/legal-entities/IntercompanyRulesView.vue'),
    meta: {
      titleKey: 'enterprise.legalEntities.routeTitles.intercompany',
      permission: LEGAL_ENTITY_PERMISSIONS.intercompanyRead,
    },
  },
  {
    path: `${LEGAL_ENTITIES_BASE}/elimination-rules`,
    name: 'enterprise-elimination-rules',
    component: () => import('@/modules/enterprise/legal-entities/EliminationRulesView.vue'),
    meta: {
      titleKey: 'enterprise.legalEntities.routeTitles.eliminationRules',
      permission: LEGAL_ENTITY_PERMISSIONS.intercompanyRead,
    },
  },
  {
    path: `${LEGAL_ENTITIES_BASE}/:id(\\d+)`,
    name: 'enterprise-legal-entity',
    component: () => import('@/modules/enterprise/legal-entities/LegalEntityView.vue'),
    meta: {
      titleKey: 'enterprise.legalEntities.routeTitles.detail',
      permission: LEGAL_ENTITY_PERMISSIONS.read,
    },
  },
]
